import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { ThemeConfig, Todo } from '../types';
import { firebaseService } from '../FirebaseService';

interface TodoListProps {
  theme: ThemeConfig;
  todos: Todo[];
  setTodos: React.Dispatch<React.SetStateAction<Todo[]>>;
}

const TodoList: React.FC<TodoListProps> = ({ theme, todos, setTodos }) => {
  const [newTodoText, setNewTodoText] = useState('');

  const toggleTodo = (id: string) => {
    setTodos(todos.map(todo => {
      if (todo.id === id) {
        const updated = { ...todo, done: !todo.done };
        firebaseService.updateTodo(id, { done: updated.done });
        return updated;
      }
      return todo;
    }));
  };

  const addTodo = () => {
    if (newTodoText.trim()) {
      const newTodo: Todo = {
        id: new Date().toISOString(),
        text: newTodoText,
        done: false,
        createdAt: new Date()
      };
      setTodos([...todos, newTodo]);
      firebaseService.saveTodo(newTodo);
      setNewTodoText('');
    }
  };

  const deleteTodo = (id: string) => {
    setTodos(todos.filter(t => t.id !== id));
    firebaseService.deleteTodo(id);
  };

  return (
    <div>
      <h2 className={`text-2xl font-bold ${theme.text} mb-6`}>
        Your To-Do List ✓
      </h2>
      
      <div className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-4 shadow-lg ${theme.shadow} border ${theme.border} mb-6 max-w-2xl`}>
        <div className="flex gap-3">
          <input
            type="text"
            value={newTodoText}
            onChange={(e) => setNewTodoText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addTodo()}
            placeholder="Add a new to-do..."
            className={`flex-1 p-3 border ${theme.border} rounded-lg ${theme.text}`}
          />
          <button
            onClick={addTodo}
            className={`${theme.button} text-white px-6 py-3 rounded-lg transition-all flex items-center gap-2`}
          >
            <Plus className="w-5 h-5" />
            Add
          </button>
        </div>
      </div>

      <div className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-6 shadow-lg ${theme.shadow} border ${theme.border} max-w-2xl`}>
        {todos.length > 0 ? (
          <div className="space-y-3">
            {todos.map(todo => (
              <div key={todo.id} className={`flex items-center gap-3 py-3 px-4 ${theme.secondary} rounded-lg hover:${theme.primary} transition-all group`}>
                <input 
                  type="checkbox" 
                  checked={todo.done}
                  className="w-5 h-5 cursor-pointer"
                  onChange={() => toggleTodo(todo.id)}
                />
                <span className={`flex-1 ${todo.done ? 'line-through opacity-60' : ''} ${theme.text}`}>
                  {todo.text}
                </span>
                <button
                  onClick={() => deleteTodo(todo.id)}
                  className={`opacity-0 group-hover:opacity-100 ${theme.accent} text-white p-1 rounded transition-all`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className={`${theme.textLight} text-center py-8`}>
            No to-dos yet. Add one above! ✨
          </p>
        )}
      </div>
    </div>
  );
};

export default TodoList;